import * as LabelPrimitive from '@radix-ui/react-label';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import {
  forwardRef,
  useId,
  useState,
  type ComponentPropsWithoutRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '@/lib/utils';

const baseCampo =
  'w-full rounded-md border border-border bg-surface px-3 text-[14px] text-foreground placeholder:text-foreground-muted/70 transition-colors focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/25 disabled:opacity-50 aria-[invalid=true]:border-destructive';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...props }, ref) => (
  <input ref={ref} className={cn(baseCampo, 'h-9', className)} {...props} />
));
Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, ...props }, ref) => <textarea ref={ref} className={cn(baseCampo, 'min-h-20 py-2', className)} {...props} />,
);
Textarea.displayName = 'Textarea';

/** Select nativo con estilo (accesible y rápido para formularios densos). */
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  ({ className, children, ...props }, ref) => (
    <select ref={ref} className={cn(baseCampo, 'h-9 pr-8', className)} {...props}>
      {children}
    </select>
  ),
);
Select.displayName = 'Select';

export const Label = forwardRef<HTMLLabelElement, ComponentPropsWithoutRef<typeof LabelPrimitive.Root>>(
  ({ className, ...props }, ref) => (
    <LabelPrimitive.Root ref={ref} className={cn('text-[13px] font-medium text-foreground', className)} {...props} />
  ),
);
Label.displayName = 'Label';

export const Switch = forwardRef<HTMLButtonElement, ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>>(
  ({ className, ...props }, ref) => (
    <SwitchPrimitive.Root
      ref={ref}
      className={cn(
        'peer inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-transparent bg-muted transition-colors data-[state=checked]:bg-accent disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb className="block size-4 rounded-full bg-surface shadow-hoja transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0.5" />
    </SwitchPrimitive.Root>
  ),
);
Switch.displayName = 'Switch';

interface InputHoraProps {
  id?: string;
  /** Hora `HH:mm` en 24 h (como la guarda la API) o `''` si no hay hora. */
  value: string;
  onChange: (hora: string) => void;
  /** Minutos entre cada opción. */
  pasoMin?: number;
  disabled?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
}

const dosDigitos = (n: number) => String(n).padStart(2, '0');

/**
 * Hora en formato de 12 horas (hora · minutos · a.m./p.m.). No usa `<input type="time">`
 * porque ese control se muestra en 12 o 24 h según el idioma del navegador.
 */
export function InputHora({ id, value, onChange, pasoMin = 5, disabled, ...aria }: InputHoraProps) {
  const coincide = /^(\d{2}):(\d{2})$/.exec(value);
  const h24 = coincide ? Number(coincide[1]) : null;
  const [parcial, setParcial] = useState<{ hora: string; minuto: string; periodo: 'am' | 'pm' }>({ hora: '', minuto: '', periodo: 'am' });
  // Con una hora completa manda el valor; sin ella se conserva lo que se lleva elegido.
  const hora = h24 === null ? parcial.hora : String(h24 % 12 || 12);
  const minuto = coincide ? coincide[2]! : parcial.minuto;
  const periodo = h24 === null ? parcial.periodo : h24 < 12 ? 'am' : 'pm';

  const minutos = Array.from({ length: Math.ceil(60 / pasoMin) }, (_, i) => dosDigitos(i * pasoMin));
  if (minuto && !minutos.includes(minuto)) minutos.push(minuto);
  minutos.sort();

  const cambiar = (cambio: Partial<typeof parcial>) => {
    const siguiente = { hora, minuto, periodo, ...cambio };
    setParcial(siguiente);
    if (!siguiente.hora) return onChange('');
    const minutoFinal = siguiente.minuto || '00';
    const h = (Number(siguiente.hora) % 12) + (siguiente.periodo === 'pm' ? 12 : 0);
    onChange(`${dosDigitos(h)}:${minutoFinal}`);
  };

  return (
    <div role="group" aria-label="Hora" className="flex items-center gap-1.5">
      <Select id={id} aria-label="Hora" {...aria} disabled={disabled} value={hora} onChange={(e) => cambiar({ hora: e.target.value })} className="cifras w-[4.5rem] px-2">
        <option value="">--</option>
        {Array.from({ length: 12 }, (_, i) => String(i + 1)).map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </Select>
      <span aria-hidden className="text-foreground-muted">
        :
      </span>
      <Select aria-label="Minutos" disabled={disabled} value={minuto || (hora ? '00' : '')} onChange={(e) => cambiar({ minuto: e.target.value })} className="cifras w-[4.5rem] px-2">
        {!hora && <option value="">--</option>}
        {minutos.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </Select>
      <Select aria-label="a.m. o p.m." disabled={disabled} value={periodo} onChange={(e) => cambiar({ periodo: e.target.value as 'am' | 'pm' })} className="w-[5.5rem] px-2">
        <option value="am">a.m.</option>
        <option value="pm">p.m.</option>
      </Select>
    </div>
  );
}

interface CampoProps {
  etiqueta: string;
  error?: string;
  ayuda?: string;
  requerido?: boolean;
  className?: string;
  children: (props: { id: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string; 'aria-required'?: boolean }) => ReactNode;
}

/** Etiqueta visible + control + ayuda/error debajo, con los atributos ARIA conectados. */
export function Campo({ etiqueta, error, ayuda, requerido, className, children }: CampoProps) {
  const id = useId();
  const descripcion = error ? `${id}-error` : ayuda ? `${id}-ayuda` : undefined;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={id} className={requerido ? "after:ml-0.5 after:text-destructive after:content-['*']" : undefined}>
        {etiqueta}
      </Label>
      {children({ id, 'aria-invalid': error ? true : undefined, 'aria-describedby': descripcion, 'aria-required': requerido || undefined })}
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-[12.5px] text-destructive">
          {error}
        </p>
      ) : ayuda ? (
        <p id={`${id}-ayuda`} className="text-[12.5px] text-foreground-muted">
          {ayuda}
        </p>
      ) : null}
    </div>
  );
}
